// Google Gemini AI Service for direct API calls from frontend
class GoogleAIService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    this.model = 'gemini-2.0-flash-exp'; // Используем быструю модель Gemini
    this.baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models';
  }

  /**
   * Исправляет грамматические, орфографические и пунктуационные ошибки в тексте
   */
  async fixGrammarErrors(text, language = 'ru') {
    if (!text || text.length === 0) return text;
    
    if (!this.apiKey) {
      console.error('Google API key не настроен');
      return text;
    }

    try {
      const prompt = `Исправь ВСЕ грамматические, орфографические и пунктуационные ошибки в следующем тексте. 
Сохрани смысл и структуру текста. Верни ТОЛЬКО исправленный текст, БЕЗ дополнительных комментариев.

ТЕКСТ:
"${text}"

ТРЕБОВАНИЯ:
- Исправь орфографические ошибки
- Исправь грамматические ошибки
- Добавь правильную пунктуацию (точки, запятые)
- Сохрани исходный смысл и стиль
- НЕ добавляй никаких комментариев или пояснений
- Верни ТОЛЬКО исправленный текст

ИСПРАВЛЕННЫЙ ТЕКСТ:`;

      const url = `${this.baseUrl}/${this.model}:generateContent?key=${this.apiKey}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: prompt }]
          }],
          generationConfig: {
            temperature: 0.3,
            maxOutputTokens: text.length + 200
          }
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Google AI grammar fix error:', error);
        
        // Проверяем на блокировку по геолокации
        if (response.status === 403 || response.status === 451) {
          console.warn('⚠️ Google AI API может быть заблокирован в вашем регионе');
        }
        
        return text;
      }

      const data = await response.json();
      const correctedText = data.candidates[0].content.parts[0].text.trim();
      
      // Убираем кавычки, если они есть
      return correctedText.replace(/^["']|["']$/g, '').trim();
    } catch (error) {
      console.error('Error fixing grammar with Google AI:', error);
      
      // Проверяем на CORS ошибки (часто при блокировке)
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.warn('⚠️ Возможна блокировка Google API или проблемы с CORS. Попробуйте VPN.');
      }
      
      return text;
    }
  }

  /**
   * Генерирует петицию из голосового транскрипта
   */
  async generatePetitionFromVoice(voiceTranscript, language = 'ru') {
    
    if (!this.apiKey) {
      throw new Error('Google API key не настроен');
    }

    if (!voiceTranscript || voiceTranscript.trim().length === 0) {
      throw new Error('Голосовой транскрипт пустой');
    }

    try {
      // ШАГ 1: Исправление грамматики
      const correctedTranscript = await this.fixGrammarErrors(voiceTranscript, language);

      // ШАГ 2: Исправление и улучшение текста
      const prompt = `Ты - помощник для исправления текста. Твоя задача - ТОЛЬКО исправить ошибки и создать заголовок. НЕ добавляй ничего нового.

ИСХОДНЫЙ ТЕКСТ:
"${correctedTranscript}"

ЗАДАЧА:
1. Понять главную мысль текста
2. Создать краткий заголовок (3-8 слов) на основе главной мысли
3. Исправить грамматические, орфографические и пунктуационные ошибки в тексте
4. Улучшить формулировки для читаемости, но НЕ менять смысл

СТРОГИЕ ПРАВИЛА:
- НЕ добавляй новой информации, которой нет в оригинале
- НЕ добавляй свои мнения, выводы или предложения
- НЕ добавляй формальные обращения ("Уважаемые", "Обращаюсь к вам" и т.д.)
- НЕ добавляй заключительные фразы ("С уважением", "Прошу рассмотреть" и т.д.)
- НЕ меняй факты и данные из оригинала
- Только исправляй ошибки: грамматика, орфография, пунктуация
- Улучшай формулировки для читаемости, но сохраняй все слова и идеи оригинала
- Структурируй текст на абзацы (2-4 абзаца) через \\n\\n, если текст длинный
- НЕ используй маркированные списки
- Сохрани стиль и тон оригинала

ФОРМАТ ОТВЕТА:
Верни ТОЛЬКО валидный JSON без дополнительного текста:
{
  "title": "Краткий заголовок на основе главной мысли (3-8 слов)",
  "description": "Исправленный текст с теми же фактами и идеями, только без ошибок"
}

ПРИМЕР:
Оригинал: "нужно отремонтировать дорогу на улице ленина она очень плохая"
Ответ:
{
  "title": "Ремонт дороги на улице Ленина",
  "description": "Необходимо отремонтировать дорогу на улице Ленина. Дорожное покрытие находится в очень плохом состоянии."
}

ВАЖНО: Верни ТОЛЬКО JSON, без markdown, без комментариев, без дополнительного текста.`;

      const url = `${this.baseUrl}/${this.model}:generateContent?key=${this.apiKey}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: prompt }]
          }],
          generationConfig: {
            temperature: 0.2,  // Низкая температура для точности и отсутствия выдумок
            maxOutputTokens: 2048
          }
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Google AI petition generation error:', error);
        
        // Проверяем на блокировку по геолокации
        if (response.status === 403 || response.status === 451) {
          throw new Error('Google AI API заблокирован в вашем регионе. Попробуйте использовать VPN или обратитесь в поддержку.');
        }
        
        throw new Error(`Google AI вернул статус ${response.status}`);
      }

      const data = await response.json();
      const generatedText = data.candidates[0].content.parts[0].text.trim();
      

      // Парсим JSON ответ
      let parsedResponse;
      try {
        // Убираем markdown блоки если есть
        let jsonText = generatedText.trim();
        if (jsonText.startsWith('```json')) {
          jsonText = jsonText.substring(7);  // Убираем ```json
        }
        if (jsonText.endsWith('```')) {
          jsonText = jsonText.substring(0, jsonText.length - 3);  // Убираем ```
        }
        jsonText = jsonText.trim();
        
        parsedResponse = JSON.parse(jsonText);
        
        // Проверяем наличие обязательных полей
        if (!parsedResponse.title || !parsedResponse.description) {
          throw new Error('Отсутствуют обязательные поля в ответе');
        }
        
        // Очищаем заголовок от формальных фраз
        let title = parsedResponse.title.trim();
        
        // Удаляем формальные фразы в начале
        const formalPhrases = [
          /^создать\s+петицию\s+/i,
          /^создай\s+петицию\s+/i,
          /^петиция\s+(о|насчёт|на\s+счёт|про)\s+/i,
          /^мы\s+требуем\s+/i,
          /^прошу\s+(рассмотреть|создать)\s+/i,
        ];
        for (const phrase of formalPhrases) {
          title = title.replace(phrase, '').trim();
        }
        
        // Если заголовок содержит несколько тем через "и", берем только первую
        if (title.toLowerCase().includes(' и ')) {
          const parts = title.split(/\s+и\s+/i);
          if (parts.length > 1) {
            title = parts[0].trim();
          }
        }
        
        parsedResponse.title = title;
        
        return {
          generated_content: parsedResponse,
          success: true
        };
        
      } catch (parseError) {
        console.error('❌ Ошибка парсинга JSON:', parseError);
        console.error('Ответ от AI:', generatedText);
        
        // Fallback - создаем базовую петицию без добавления лишнего
        // Просто используем исправленный текст как описание и создаем простой заголовок
        const words = correctedTranscript.trim().split(/\s+/).slice(0, 6).join(' ');
        const simpleTitle = words.length > 50 ? words.substring(0, 50) + '...' : words;
        
        return {
          generated_content: {
            title: simpleTitle || 'Инициатива на основе голосового сообщения',
            description: correctedTranscript.trim()
          },
          success: true,
          note: 'Использован базовый шаблон из-за ошибки парсинга AI ответа'
        };
      }
      
    } catch (error) {
      console.error('❌ Ошибка генерации петиции через Google AI:', error);
      
      // Проверяем на CORS ошибки (часто при блокировке)
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Google AI API недоступен (возможна блокировка в вашем регионе). Попробуйте использовать VPN.');
      }
      
      throw error;
    }
  }
}

const googleAIService = new GoogleAIService();
export default googleAIService;

